<template>
  <div :id="id" class="modal fade">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <modal-close-button @confirmed="resetAndClose" />
          <h4>
            <font-awesome-icon
              icon="fa-duotone fa-file-invoice-dollar"
              class="mr-2"
            />
            Vermittlungsgebühr-Abrechnung
          </h4>
        </div>

        <div class="modal-body">
          <!-- Schritt 1: Zeitraum & Aktion wählen -->
          <div v-if="step === 'auswahl'">
            <div class="row mb-3">
              <div class="col-md-4">
                <div class="form-group">
                  <label>Von</label>
                  <input v-model="von" type="date" class="form-control" />
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label>Bis</label>
                  <input v-model="bis" type="date" class="form-control" />
                </div>
              </div>
            </div>

            <div class="row">
              <div :class="darfAbrechnen ? 'col-md-6' : 'col-md-12'">
                <div
                  class="card p-3 mb-2 pointer abrechnung-card"
                  :class="{ 'border-primary': selectedAction === 'check' }"
                  @click="selectedAction = 'check'"
                >
                  <h5>
                    <font-awesome-icon
                      icon="fa-duotone fa-clipboard-check"
                      class="mr-2 text-info"
                    />
                    Einsätze prüfen (Check)
                  </h5>
                  <p class="mb-0 text-muted">
                    Übersicht aller Einsätze im Zeitraum gruppiert nach
                    Vermittlungsstelle und Bauernfamilie. Zeigt nicht
                    abgeschlossene Einsätze auf.
                  </p>
                </div>
              </div>
              <div v-if="darfAbrechnen" class="col-md-6">
                <div
                  class="card p-3 mb-2 pointer abrechnung-card"
                  :class="{ 'border-primary': selectedAction === 'abrechnung' }"
                  @click="selectedAction = 'abrechnung'"
                >
                  <h5>
                    <font-awesome-icon
                      icon="fa-duotone fa-file-invoice-dollar"
                      class="mr-2 text-success"
                    />
                    Abrechnung erstellen
                  </h5>
                  <p class="mb-0 text-muted">
                    Erstellt Aufträge für alle Bauernfamilien mit
                    abgeschlossenen Einsätzen. Nicht abgeschlossene Einsätze
                    werden übersprungen.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Schritt 2: Warnung vor Abrechnung -->
          <div v-if="step === 'warnung'">
            <div class="alert alert-warning">
              <font-awesome-icon
                icon="fa-duotone fa-exclamation-triangle"
                class="mr-2"
              />
              <strong>Achtung:</strong> Es werden Aufträge für alle
              Bauernfamilien erstellt, deren Einsätze im Zeitraum
              {{ formatDateDisplay(von) }} – {{ formatDateDisplay(bis) }}
              vollständig abgeschlossen sind. Dieser Vorgang kann nicht
              rückgängig gemacht werden.
            </div>
            <p>Möchten Sie die Abrechnung wirklich durchführen?</p>
          </div>

          <!-- Schritt 3: Loading -->
          <div v-if="step === 'loading'" class="text-center py-5">
            <font-awesome-icon
              icon="fa-duotone fa-spinner"
              spin
              size="3x"
              class="text-primary"
            />
            <p class="mt-3">{{ loadingText }}</p>
          </div>

          <!-- Schritt 4: Check-Ergebnis -->
          <div v-if="step === 'check-result'">
            <!-- Zusammenfassung -->
            <div
              v-if="checkResult && checkResult.vermittlungsstellen.length > 0"
              class="mb-3"
            >
              <div class="alert alert-secondary py-2 mb-3">
                <strong>Zusammenfassung:</strong>
                {{ checkResult.vermittlungsstellen.length }}
                Vermittlungsstellen, {{ totalBauern }} Bauernfamilien,
                {{ totalEinsaetze }} Einsätze
                <span v-if="totalOffene > 0" class="text-danger ml-2">
                  ({{ totalOffene }} nicht abgeschlossen)
                </span>
                <span v-if="totalUnvollstaendig > 0" class="text-warning ml-2">
                  ({{ totalUnvollstaendig }} unvollständig)
                </span>
                <span
                  v-if="totalOffene === 0 && totalUnvollstaendig === 0"
                  class="text-success ml-2"
                >
                  (alle verrechenbar)
                </span>
              </div>

              <!-- Alle auf-/zuklappen -->
              <div class="mb-2">
                <button
                  class="btn btn-sm btn-outline-secondary mr-2"
                  @click="expandAll"
                >
                  <font-awesome-icon
                    icon="fa-duotone fa-angles-down"
                    class="mr-1"
                  />
                  Alle aufklappen
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="collapseAll"
                >
                  <font-awesome-icon
                    icon="fa-duotone fa-angles-up"
                    class="mr-1"
                  />
                  Alle zuklappen
                </button>
              </div>

              <!-- VS-Ebene -->
              <div
                v-for="(vs, vsIndex) in checkResult.vermittlungsstellen"
                :key="vs.id || 'unknown-' + vsIndex"
                class="card mb-2"
              >
                <div
                  class="card-header pointer d-flex justify-content-between align-items-center py-2"
                  :class="
                    vsStatus(vs) === 'rot'
                      ? 'bg-danger-light'
                      : vsStatus(vs) === 'gelb'
                      ? 'bg-warning-light'
                      : 'bg-success-light'
                  "
                  @click="toggleVs(vsIndex)"
                >
                  <div>
                    <font-awesome-icon
                      :icon="
                        expandedVs[vsIndex]
                          ? 'fa-solid fa-chevron-down'
                          : 'fa-regular fa-chevron-right'
                      "
                      class="mr-2"
                    />
                    <font-awesome-icon
                      icon="fa-duotone fa-building"
                      class="mr-2"
                    />
                    <strong>{{ vs.bezeichnung }}</strong>
                  </div>
                  <div class="d-flex align-items-center">
                    <button
                      class="btn btn-sm btn-outline-secondary mr-2"
                      @click.stop="toggleVsBauern(vsIndex, vs)"
                      :title="
                        vsAllBauernExpanded(vs, vsIndex)
                          ? 'Alle Höfe zuklappen'
                          : 'Alle Höfe aufklappen'
                      "
                    >
                      <font-awesome-icon
                        :icon="
                          vsAllBauernExpanded(vs, vsIndex)
                            ? 'fa-duotone fa-angles-up'
                            : 'fa-duotone fa-angles-down'
                        "
                      />
                    </button>
                    <span class="badge badge-secondary mr-1"
                      >{{ vs.bauernfamilien.length }} Höfe</span
                    >
                    <span class="badge badge-info mr-1"
                      >{{ vsEinsatzCount(vs) }} Einsätze</span
                    >
                    <span
                      v-if="vsOffeneCount(vs) > 0"
                      class="badge badge-danger mr-1"
                      >{{ vsOffeneCount(vs) }} offen</span
                    >
                    <span
                      v-if="vsUnvollstaendigCount(vs) > 0"
                      class="badge badge-warning"
                      >{{ vsUnvollstaendigCount(vs) }} unvollständig</span
                    >
                    <span
                      v-if="
                        vsOffeneCount(vs) === 0 &&
                        vsUnvollstaendigCount(vs) === 0
                      "
                      class="badge badge-success"
                      >alle ok</span
                    >
                  </div>
                </div>

                <!-- Bauern-Ebene -->
                <div v-show="expandedVs[vsIndex]" class="card-body p-0">
                  <div
                    v-for="(bauer, bauerIndex) in vs.bauernfamilien"
                    :key="bauer.firma_id"
                    class="border-bottom"
                  >
                    <div
                      class="pointer d-flex justify-content-between align-items-center px-3 py-2"
                      :class="
                        bauerStatus(bauer) === 'rot'
                          ? 'bg-danger-lighter'
                          : bauerStatus(bauer) === 'gelb'
                          ? 'bg-warning-lighter'
                          : 'bg-success-lighter'
                      "
                      @click="toggleBauer(vsIndex, bauerIndex)"
                    >
                      <div>
                        <font-awesome-icon
                          :icon="
                            expandedBauern[vsIndex + '-' + bauerIndex]
                              ? 'fa-solid fa-chevron-down'
                              : 'fa-regular fa-chevron-right'
                          "
                          class="mr-2"
                        />
                        <font-awesome-icon
                          icon="fa-duotone fa-tractor"
                          class="mr-1"
                        />
                        {{ bauer.firma_name }}
                      </div>
                      <div>
                        <span class="badge badge-info mr-1"
                          >{{ bauer.einsaetze.length }} Einsätze</span
                        >
                        <span
                          v-if="bauerOffeneCount(bauer) > 0"
                          class="badge badge-danger mr-1"
                          >{{ bauerOffeneCount(bauer) }} offen</span
                        >
                        <span
                          v-if="bauerUnvollstaendigCount(bauer) > 0"
                          class="badge badge-warning"
                          >{{ bauerUnvollstaendigCount(bauer) }} unvollständig</span
                        >
                        <span
                          v-if="
                            bauerOffeneCount(bauer) === 0 &&
                            bauerUnvollstaendigCount(bauer) === 0
                          "
                          class="badge badge-success"
                          >alle ok</span
                        >
                      </div>
                    </div>

                    <!-- Einsatz-Tabelle -->
                    <div
                      v-show="expandedBauern[vsIndex + '-' + bauerIndex]"
                      class="px-3 pb-2"
                    >
                      <table class="table table-sm table-bordered mb-0 mt-1">
                        <thead>
                          <tr>
                            <th style="width: 15%">Status</th>
                            <th style="width: 20%">Jugendliche/r</th>
                            <th style="width: 12%">Wohnort</th>
                            <th style="width: 12%">Von</th>
                            <th style="width: 12%">Bis</th>
                            <th style="width: 12%">Arbeitstage</th>
                            <th style="width: 5%">AF</th>
                            <th style="width: 5%"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="e in bauer.einsaetze"
                            :key="e.einsatz_id"
                            :class="{
                              'table-danger': einsatzStatus(e) === 'rot',
                              'table-warning': einsatzStatus(e) === 'gelb',
                              'table-success': einsatzStatus(e) === 'gruen',
                            }"
                          >
                            <td>
                              <font-awesome-icon
                                :icon="
                                  e.ist_abgeschlossen
                                    ? 'fa-solid fa-check'
                                    : 'fa-duotone fa-times-circle'
                                "
                                :class="
                                  e.ist_abgeschlossen
                                    ? 'text-success'
                                    : 'text-danger'
                                "
                                class="mr-1"
                              />
                              {{ e.status.bezeichnung }}
                            </td>
                            <td>{{ e.jugendlicher_name }}</td>
                            <td>{{ e.jugendlicher_wohnort || "-" }}</td>
                            <td>{{ e.von || "-" }}</td>
                            <td>{{ e.bis || "-" }}</td>
                            <td>
                              {{
                                e.arbeitstage_sackgeld != null
                                  ? e.arbeitstage_sackgeld
                                  : "-"
                              }}
                            </td>
                            <td class="text-center">
                              <font-awesome-icon
                                :icon="
                                  e.hat_abschlussformular
                                    ? 'fa-solid fa-check'
                                    : 'fa-duotone fa-times-circle'
                                "
                                :class="
                                  e.hat_abschlussformular
                                    ? 'text-success'
                                    : 'text-danger'
                                "
                              />
                            </td>
                            <td class="text-center">
                              <a
                                :href="e.einsatz_link"
                                target="_blank"
                                title="Einsatz öffnen"
                              >
                                <font-awesome-icon
                                  icon="fa-duotone fa-external-link"
                                />
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="alert alert-info">
              Keine Einsätze im gewählten Zeitraum gefunden.
            </div>
          </div>

          <!-- Schritt 5: Abrechnungs-Ergebnis -->
          <div v-if="step === 'abrechnung-result'">
            <div v-if="abrechnungResult">
              <!-- Erfolgreich -->
              <div v-if="abrechnungResult.erfolgreich.length > 0" class="mb-4">
                <h5 class="text-success">
                  <font-awesome-icon icon="fa-solid fa-check" class="mr-2" />
                  Erfolgreich erstellt ({{
                    abrechnungResult.erfolgreich.length
                  }})
                </h5>
                <table class="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th style="width: 40%">Bauernfamilie</th>
                      <th style="width: 15%">Positionen</th>
                      <th style="width: 20%">Total Brutto</th>
                      <th style="width: 25%">Auftrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="e in abrechnungResult.erfolgreich"
                      :key="e.auftrag_id"
                      class="table-success"
                    >
                      <td>{{ e.firma_name }}</td>
                      <td>{{ e.anzahl_positionen }}</td>
                      <td>CHF {{ e.total_brutto.toFixed(2) }}</td>
                      <td>
                        <a
                          :href="e.auftrag_link"
                          target="_blank"
                          title="Auftrag öffnen"
                        >
                          <font-awesome-icon
                            icon="fa-duotone fa-external-link"
                            class="mr-1"
                          />
                          Auftrag öffnen
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Warnungen -->
              <div v-if="erfolgreichMitWarnungen.length > 0" class="mb-4">
                <h5 class="text-warning">
                  <font-awesome-icon
                    icon="fa-duotone fa-exclamation-triangle"
                    class="mr-2"
                  />
                  Warnungen ({{ erfolgreichMitWarnungen.length }})
                </h5>
                <div
                  v-for="e in erfolgreichMitWarnungen"
                  :key="'warn-' + e.auftrag_id"
                  class="alert alert-warning py-2 mb-2"
                >
                  <strong>{{ e.firma_name }}</strong>
                  <a
                    :href="e.auftrag_link"
                    target="_blank"
                    class="ml-2"
                    title="Auftrag öffnen"
                  >
                    <font-awesome-icon
                      icon="fa-duotone fa-external-link"
                      class="mr-1"
                    />
                    Auftrag
                  </a>
                  <ul class="mb-0 mt-1">
                    <li v-for="(w, wi) in e.warnungen" :key="wi">{{ w }}</li>
                  </ul>
                </div>
              </div>

              <!-- Fehlgeschlagen -->
              <div v-if="abrechnungResult.fehlgeschlagen.length > 0">
                <h5 class="text-danger">
                  <font-awesome-icon
                    icon="fa-duotone fa-exclamation-circle"
                    class="mr-2"
                  />
                  Nicht erstellt ({{ abrechnungResult.fehlgeschlagen.length }})
                </h5>
                <div
                  v-for="f in abrechnungResult.fehlgeschlagen"
                  :key="f.firma_id"
                  class="card border-danger mb-2"
                >
                  <div class="card-body p-2">
                    <strong>{{ f.firma_name }}</strong>
                    <span class="text-muted ml-2">– {{ f.message }}</span>

                    <table
                      v-if="f.offene_einsaetze.length > 0"
                      class="table table-sm mt-2 mb-0"
                    >
                      <thead>
                        <tr>
                          <th style="width: 35%">Jugendliche/r</th>
                          <th style="width: 20%">Status</th>
                          <th style="width: 30%">Grund</th>
                          <th style="width: 15%"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="oe in f.offene_einsaetze"
                          :key="oe.einsatz_id"
                        >
                          <td>{{ oe.jugendlicher_name }}</td>
                          <td>{{ oe.status.bezeichnung }}</td>
                          <td>
                            <span class="badge badge-danger">{{
                              oe.grund
                            }}</span>
                          </td>
                          <td class="text-center">
                            <a
                              :href="oe.einsatz_link"
                              target="_blank"
                              title="Einsatz öffnen"
                            >
                              <font-awesome-icon
                                icon="fa-duotone fa-external-link"
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Zusammenfassung -->
              <div
                v-if="
                  abrechnungResult.erfolgreich.length === 0 &&
                  abrechnungResult.fehlgeschlagen.length === 0
                "
                class="alert alert-info"
              >
                Keine Einsätze im gewählten Zeitraum gefunden.
              </div>
            </div>
          </div>

          <!-- Fehler -->
          <div v-if="step === 'error'" class="alert alert-danger">
            <font-awesome-icon
              icon="fa-duotone fa-exclamation-triangle"
              class="mr-2"
            />
            {{ errorMessage }}
          </div>
        </div>

        <div class="modal-footer">
          <!-- Auswahl-Schritt -->
          <template v-if="step === 'auswahl'">
            <button class="btn btn-default" @click="resetAndClose">
              Abbrechen
            </button>
            <button
              class="btn btn-primary"
              :disabled="!selectedAction || !von || !bis"
              @click="executeAction"
            >
              <font-awesome-icon
                :icon="
                  selectedAction === 'check'
                    ? 'fa-duotone fa-clipboard-check'
                    : 'fa-duotone fa-file-invoice-dollar'
                "
                class="mr-1"
              />
              {{ selectedAction === "check" ? "Check starten" : "Weiter" }}
            </button>
          </template>

          <!-- Warnung-Schritt -->
          <template v-if="step === 'warnung'">
            <button class="btn btn-default" @click="step = 'auswahl'">
              Zurück
            </button>
            <button class="btn btn-danger" @click="executeAbrechnung">
              <font-awesome-icon
                icon="fa-duotone fa-file-invoice-dollar"
                class="mr-1"
              />
              Abrechnung durchführen
            </button>
          </template>

          <!-- Ergebnis-Schritte -->
          <template
            v-if="
              step === 'check-result' ||
              step === 'abrechnung-result' ||
              step === 'error'
            "
          >
            <button class="btn btn-default" @click="backToAuswahl">
              Neue Abrechnung
            </button>
            <button class="btn btn-primary" @click="resetAndClose">
              Schliessen
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ModalCloseButton from "@/components/global/ModalCloseButton";
import AppApi from "@/api/AppApi";

export default {
  name: "AbrechnungModal",
  components: { ModalCloseButton },
  props: {
    id: {
      type: String,
      required: false,
      default: "abrechnung-modal",
    },
  },
  data() {
    return {
      shown: false,
      step: "auswahl", // auswahl | warnung | loading | check-result | abrechnung-result | error
      selectedAction: "check",
      darfAbrechnen: false, // nur Geschäftsstelle – wird beim Öffnen vom Backend geladen
      von: this.getDefaultVon(),
      bis: this.getDefaultBis(),
      checkResult: null,
      abrechnungResult: null,
      errorMessage: "",
      loadingText: "",
      expandedVs: {},
      expandedBauern: {},
    };
  },
  computed: {
    totalBauern() {
      if (!this.checkResult) return 0;
      return this.checkResult.vermittlungsstellen.reduce(
        (sum, vs) => sum + vs.bauernfamilien.length,
        0
      );
    },
    totalEinsaetze() {
      if (!this.checkResult) return 0;
      return this.checkResult.vermittlungsstellen.reduce(
        (sum, vs) =>
          sum + vs.bauernfamilien.reduce((s, b) => s + b.einsaetze.length, 0),
        0
      );
    },
    totalUnvollstaendig() {
      if (!this.checkResult) return 0;
      return this.checkResult.vermittlungsstellen.reduce(
        (sum, vs) => sum + this.vsUnvollstaendigCount(vs),
        0
      );
    },
    erfolgreichMitWarnungen() {
      if (!this.abrechnungResult) return [];
      return this.abrechnungResult.erfolgreich.filter(
        (e) => e.warnungen && e.warnungen.length > 0
      );
    },
    totalOffene() {
      if (!this.checkResult) return 0;
      return this.checkResult.vermittlungsstellen.reduce(
        (sum, vs) =>
          sum +
          vs.bauernfamilien.reduce(
            (s, b) =>
              s + b.einsaetze.filter((e) => !e.ist_abgeschlossen).length,
            0
          ),
        0
      );
    },
  },
  mounted() {
    this.$bus.$on("open-modal", (id) => {
      if (id === this.id && !this.shown) this.openModal(id);
    });
  },
  methods: {
    getDefaultVon() {
      const now = new Date();
      // Wenn wir vor November sind, nehme Vorjahr November, sonst aktuelles Jahr
      const year =
        now.getMonth() < 10 ? now.getFullYear() - 1 : now.getFullYear();
      return `${year}-11-01`;
    },
    getDefaultBis() {
      const now = new Date();
      // Wenn wir vor November sind, nehme aktuelles Jahr Oktober, sonst nächstes Jahr
      const year =
        now.getMonth() < 10 ? now.getFullYear() : now.getFullYear() + 1;
      return `${year}-10-31`;
    },

    openModal(id) {
      this.shown = true;
      const thisId = id ? id : this.id;
      $("#" + thisId).modal({
        backdrop: "static",
        keyboard: false,
      });
      this.ladeBerechtigung();
    },

    async ladeBerechtigung() {
      // Nur die Geschäftsstelle darf Aufträge erstellen – VS sehen nur den Check
      try {
        const response = await AppApi.get("abrechnung-berechtigung-agriviva");
        this.darfAbrechnen = !!response.data?.darf_abrechnen;
      } catch (e) {
        this.darfAbrechnen = false;
      }
      if (!this.darfAbrechnen) this.selectedAction = "check";
    },

    closeModal() {
      this.shown = false;
      $("#" + this.id).modal("hide");
    },

    resetAndClose() {
      this.step = "auswahl";
      this.checkResult = null;
      this.abrechnungResult = null;
      this.errorMessage = "";
      this.expandedVs = {};
      this.expandedBauern = {};
      this.closeModal();
    },

    backToAuswahl() {
      this.step = "auswahl";
      this.checkResult = null;
      this.abrechnungResult = null;
      this.errorMessage = "";
      this.expandedVs = {};
      this.expandedBauern = {};
    },

    formatDateForApi(dateStr) {
      // input: yyyy-MM-dd → output: dd-MM-yyyy
      if (!dateStr) return "";
      const parts = dateStr.split("-");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    },

    formatDateDisplay(dateStr) {
      // input: yyyy-MM-dd → output: dd.MM.yyyy
      if (!dateStr) return "";
      const parts = dateStr.split("-");
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    },

    executeAction() {
      // Ohne Berechtigung immer nur der Check (Backend lehnt die Abrechnung zusätzlich mit 403 ab)
      if (this.selectedAction === "check" || !this.darfAbrechnen) {
        this.executeCheck();
      } else {
        this.step = "warnung";
      }
    },

    async executeCheck() {
      this.step = "loading";
      this.loadingText = "Einsätze werden geprüft...";

      try {
        const von = this.formatDateForApi(this.von);
        const bis = this.formatDateForApi(this.bis);
        const response = await AppApi.get(
          `check-abrechnung-agriviva?von=${von}&bis=${bis}`
        );
        this.checkResult = response.data;
        this.step = "check-result";
      } catch (e) {
        this.errorMessage =
          "Fehler beim Laden der Check-Daten: " +
          (e.response?.data?.error || e.message);
        this.step = "error";
      }
    },

    async executeAbrechnung() {
      this.step = "loading";
      this.loadingText = "Aufträge werden erstellt...";

      try {
        const von = this.formatDateForApi(this.von);
        const bis = this.formatDateForApi(this.bis);
        const response = await AppApi.get(
          `abrechnung-agriviva?von=${von}&bis=${bis}`
        );
        this.abrechnungResult = response.data;
        this.step = "abrechnung-result";
      } catch (e) {
        this.errorMessage =
          "Fehler beim Erstellen der Abrechnung: " +
          (e.response?.data?.error || e.message);
        this.step = "error";
      }
    },

    openAuftrag(link) {
      this.$router.push(link);
      this.resetAndClose();
    },
    openEinsatz(link) {
      this.$router.push(link);
      this.resetAndClose();
    },

    // Accordion
    toggleVs(index) {
      this.$set(this.expandedVs, index, !this.expandedVs[index]);
    },
    toggleBauer(vsIndex, bauerIndex) {
      const key = vsIndex + "-" + bauerIndex;
      this.$set(this.expandedBauern, key, !this.expandedBauern[key]);
    },
    expandAll() {
      if (!this.checkResult) return;
      this.checkResult.vermittlungsstellen.forEach((vs, vsIndex) => {
        this.$set(this.expandedVs, vsIndex, true);
        vs.bauernfamilien.forEach((_, bauerIndex) => {
          this.$set(this.expandedBauern, vsIndex + "-" + bauerIndex, true);
        });
      });
    },
    collapseAll() {
      this.expandedVs = {};
      this.expandedBauern = {};
    },
    toggleVsBauern(vsIndex, vs) {
      const allOpen = this.vsAllBauernExpanded(vs, vsIndex);
      // Beim Aufklappen die VS selbst offen halten, damit die Höfe sichtbar sind
      if (!allOpen) this.$set(this.expandedVs, vsIndex, true);
      vs.bauernfamilien.forEach((_, bauerIndex) => {
        this.$set(this.expandedBauern, vsIndex + "-" + bauerIndex, !allOpen);
      });
    },
    vsAllBauernExpanded(vs, vsIndex) {
      return (
        vs.bauernfamilien.length > 0 &&
        vs.bauernfamilien.every(
          (_, bauerIndex) => !!this.expandedBauern[vsIndex + "-" + bauerIndex]
        )
      );
    },

    // Status-Helper
    vsEinsatzCount(vs) {
      return vs.bauernfamilien.reduce((sum, b) => sum + b.einsaetze.length, 0);
    },
    vsOffeneCount(vs) {
      return vs.bauernfamilien.reduce(
        (sum, b) =>
          sum + b.einsaetze.filter((e) => !e.ist_abgeschlossen).length,
        0
      );
    },
    vsHasOffene(vs) {
      return this.vsOffeneCount(vs) > 0;
    },
    bauerOffeneCount(bauer) {
      return bauer.einsaetze.filter((e) => !e.ist_abgeschlossen).length;
    },
    bauerHasOffene(bauer) {
      return this.bauerOffeneCount(bauer) > 0;
    },
    bauerUnvollstaendigCount(bauer) {
      return bauer.einsaetze.filter((e) => this.einsatzStatus(e) === "gelb")
        .length;
    },
    vsUnvollstaendigCount(vs) {
      return vs.bauernfamilien.reduce(
        (sum, b) => sum + this.bauerUnvollstaendigCount(b),
        0
      );
    },
    // Ampel: rot = nicht abgeschlossen (warten), gelb = abgeschlossen aber AF/Arbeitstage fehlen
    // (nachtragen -> sonst nicht verrechenbar), gruen = voll verrechenbar. Deckt sich mit der
    // OK-Pruefung in AbrechungsService.createAbrechnungByDate.
    einsatzStatus(e) {
      if (!e.ist_abgeschlossen) return "rot";
      if (
        !e.hat_abschlussformular ||
        e.arbeitstage_sackgeld == null ||
        e.arbeitstage_sackgeld <= 0
      )
        return "gelb";
      return "gruen";
    },
    bauerStatus(bauer) {
      if (bauer.einsaetze.some((e) => this.einsatzStatus(e) === "rot"))
        return "rot";
      if (bauer.einsaetze.some((e) => this.einsatzStatus(e) === "gelb"))
        return "gelb";
      return "gruen";
    },
    vsStatus(vs) {
      if (this.vsOffeneCount(vs) > 0) return "rot";
      if (this.vsUnvollstaendigCount(vs) > 0) return "gelb";
      return "gruen";
    },
  },
};
</script>

<style scoped>
.abrechnung-card {
  cursor: pointer;
  transition: border-color 0.2s;
}
.abrechnung-card:hover {
  border-color: #007bff !important;
}
.pointer {
  cursor: pointer;
}
.bg-danger-light {
  background-color: #f8d7da !important;
}
.bg-success-light {
  background-color: #d4edda !important;
}
.bg-danger-lighter {
  background-color: #fce8ea !important;
}
.bg-success-lighter {
  background-color: #e8f5e9 !important;
}
.bg-warning-light {
  background-color: #fff3cd !important;
}
.bg-warning-lighter {
  background-color: #fdf6e3 !important;
}
.card-header {
  user-select: none;
}
</style>
